import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLoader } from '../../../base/context/LoaderProvider';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import { userApiService } from '../../../service/admin/userApiService';
import ReferralService from '../../../service/admin/referralService';
import { orderApiService } from '../../../service/admin/ordersApiService';
import { productApiService } from '../../../service/admin/productApiService';
import { transactionsApiService } from '../../../service/admin/transactionsApiService';
import {
  FaUsers, FaUserPlus, FaShoppingCart, FaBox, FaDollarSign,
  FaChartLine, FaLink, FaArrowUp, FaArrowDown, FaArrowRight,
  FaPlus, FaEye, FaCog, FaFileAlt, FaUserClock, FaBan,
  FaCheckCircle, FaClock, FaExclamationTriangle, FaWallet,
  FaExchangeAlt, FaTicketAlt, FaSync, FaStore, FaShoppingBag, FaCalendarAlt
} from 'react-icons/fa';
import './adminDashboard.css';

export default function AdminDashboardNew() {
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    todayUsers: 0,
    activeUsers: 0,
    totalOrders: 0,
    pendingOrders: 0,
    todayOrders: 0,
    totalProducts: 0,
    activeProducts: 0,
    draftProducts: 0,
    totalRevenue: 0,
    todayRevenue: 0,
    failedOrders: 0,
    totalTransactions: 0,
    completedOrders: 0,
    amazonOrders: 0,
    shopifyOrders: 0,
    myntraOrders: 0,
  });
  const [referralStats, setReferralStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [userRes, referralRes, dashboardStatsRes, ordersRes, productCountRes, transactionsRes] = await Promise.allSettled([
        userApiService.getTodayRegisterUser(),
        ReferralService.getDashboard(),
        orderApiService.getDashboardOrderStats(),
        orderApiService.getAllOrders('?page=1&limit=5'),
        productApiService.getProductCount(),
        transactionsApiService.getTransactions('?page=1&limit=5'),
      ]);

      // Process user stats
      if (userRes.status === 'fulfilled' && userRes.value?.status?.code === 200) {
        const userData = userRes.value.data;
        setStats(prev => ({
          ...prev,
          totalUsers: userData?.totalUser || 0,
          todayUsers: userData?.totalUserToday || 0,
          activeUsers: userData?.activeUsers || 0,
        }));
      }

      // Process referral stats
      if (referralRes.status === 'fulfilled' && referralRes.value?.status?.code === 200) {
        setReferralStats(referralRes.value.data?.overview);
      }

      // Process dashboard order stats (single source for dashboard cards)
      if (dashboardStatsRes.status === 'fulfilled' && dashboardStatsRes.value?.status?.code === 200) {
        const dashboardOrderData = dashboardStatsRes.value.data || {};
        setStats(prev => ({
          ...prev,
          totalOrders: dashboardOrderData.totalOrders || 0,
          pendingOrders: dashboardOrderData.pendingOrders || 0,
          todayOrders: dashboardOrderData.todayOrders || 0,
          completedOrders: dashboardOrderData.completedOrders || 0,
          failedOrders: dashboardOrderData.failedOrders || 0,
          amazonOrders: dashboardOrderData.amazonOrders || 0,
          shopifyOrders: dashboardOrderData.shopifyOrders || 0,
          myntraOrders: dashboardOrderData.myntraOrders || 0,
        }));
      }

      // Process recent orders
      if (ordersRes.status === 'fulfilled' && ordersRes.value?.status?.code === 200) {
        const ordersData = ordersRes.value.data;
        setRecentOrders(ordersData?.orders || []);
      }

      // Process products count
      if (productCountRes.status === 'fulfilled') {
        const res = productCountRes.value;
        if (res?.status?.code === 200 && res?.data) {
          setStats(prev => ({
            ...prev,
            totalProducts: res.data.count || 0,
            activeProducts: res.data.activeCount || 0,
            draftProducts: res.data.draftCount || 0,
          }));
        }
      }

      // Process transactions
      if (transactionsRes.status === 'fulfilled' && transactionsRes.value?.status?.code === 200) {
        const transData = transactionsRes.value.data;
        setRecentTransactions(transData?.transactions || []);
        setStats(prev => ({
          ...prev,
          totalTransactions: transData?.pagination?.total || transData?.totalTransactions || 0,
          totalRevenue: transData?.totalRevenue || 0,
        }));
      }

    } catch (err) {
      console.error('Dashboard fetch error:', err);
      message.showToast('Failed to load dashboard data', ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const shouldShowActualNumber = (num) => {
    return Number(num) >= 1000;
  };

  if (loading) {
    return (
      <div className="kiro-dashboard">
        <div className="kiro-loading">
          <div className="kiro-loading-spinner" />
        </div>
      </div>
    );
  }

  const indianToday = new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  }).format(new Date());

  return (
    <div className="kiro-dashboard">
      {/* Dashboard Header with Refresh */}
      <div className="kiro-dashboard-header">
        <button className="kiro-refresh-btn" onClick={fetchDashboardData} disabled={loading}>
          <FaSync className={loading ? 'kiro-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Main Stats */}
      <div className="kiro-stats-grid">
        <div className="kiro-stat-card" onClick={() => navigate('/admin/user-management')}>
          <div className="kiro-stat-icon kiro-blue">
            <FaUsers />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Total Users</div>
            <div className="kiro-stat-value">
              {formatNumber(stats.totalUsers)}
              <span className="kiro-stat-actual">{stats.totalUsers.toLocaleString()}</span>
            </div>
            <div className="kiro-stat-change kiro-positive">
              <FaArrowUp /> {stats.todayUsers} today
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/user-statistics')}>
          <div className="kiro-stat-icon kiro-green">
            <FaUserPlus />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Today's Registrations</div>
            <div className="kiro-stat-value">{stats.todayUsers}</div>
            <div className="kiro-stat-change kiro-positive">
              <FaUserClock /> New signups
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/order-management')}>
          <div className="kiro-stat-icon kiro-purple">
            <FaShoppingCart />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Total Orders</div>
            <div className="kiro-stat-value">
              {formatNumber(stats.totalOrders)}
              {shouldShowActualNumber(stats.totalOrders) && (
                <span className="kiro-stat-actual">({stats.totalOrders.toLocaleString()})</span>
              )}
            </div>
            <div className="kiro-stat-change kiro-positive">
              <FaClock /> {stats.pendingOrders} pending
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/order-management')}>
          <div className="kiro-stat-icon kiro-pink">
            <FaClock />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Today Orders</div>
            <div className="kiro-stat-value">
              {formatNumber(stats.todayOrders)}
              {shouldShowActualNumber(stats.todayOrders) && (
                <span className="kiro-stat-actual">({stats.todayOrders.toLocaleString()})</span>
              )}
            </div>
            <div className="kiro-stat-change kiro-positive">
              <FaCalendarAlt /> {indianToday} (IST)
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/transactions')}>
          <div className="kiro-stat-icon kiro-cyan">
            <FaExchangeAlt />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Total Transactions</div>
            <div className="kiro-stat-value">
              {formatNumber(stats.totalTransactions)}
              {shouldShowActualNumber(stats.totalTransactions) && (
                <span className="kiro-stat-actual">({stats.totalTransactions.toLocaleString()})</span>
              )}
            </div>
            <div className="kiro-stat-change kiro-positive">
              <FaWallet /> Revenue tracked
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/products/list')}>
          <div className="kiro-stat-icon kiro-orange">
            <FaBox />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Total Products</div>
            <div className="kiro-stat-value">
              {formatNumber(stats.totalProducts)}
              {shouldShowActualNumber(stats.totalProducts) && (
                <span className="kiro-stat-actual">({stats.totalProducts.toLocaleString()})</span>
              )}
            </div>
            <div className="kiro-stat-change" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <span style={{ color: '#10b981' }}><FaCheckCircle /> {stats.activeProducts} Active</span>
              <span style={{ color: '#6b7280' }}>|</span>
              <span style={{ color: '#f59e0b' }}><FaClock /> {stats.draftProducts} Draft</span>
            </div>
          </div>
        </div>
      </div>

      {/* Secondary Stats Row */}
      <div className="kiro-stats-grid">
        <div className="kiro-stat-card" onClick={() => navigate('/admin/failed-orders')}>
          <div className="kiro-stat-icon kiro-red">
            <FaBan />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Failed Orders</div>
            <div className="kiro-stat-value">{stats.failedOrders}</div>
            <div className={`kiro-stat-change ${stats.failedOrders > 0 ? 'kiro-negative' : 'kiro-positive'}`}>
              {stats.failedOrders > 0 ? <FaExclamationTriangle /> : <FaCheckCircle />}
              {stats.failedOrders > 0 ? 'Needs attention' : 'All clear'}
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/order-management?status=payment pending')}>
          <div className="kiro-stat-icon kiro-pink">
            <FaTicketAlt />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Pending Orders</div>
            <div className="kiro-stat-value">{stats.pendingOrders}</div>
            <div className="kiro-stat-change kiro-positive">
              <FaClock /> Awaiting action
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/order-management')}>
          <div className="kiro-stat-icon kiro-indigo">
            <FaCheckCircle />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Completed Orders</div>
            <div className="kiro-stat-value">
              {formatNumber(stats.completedOrders)}
              {shouldShowActualNumber(stats.completedOrders) && (
                <span className="kiro-stat-actual">({stats.completedOrders.toLocaleString()})</span>
              )}
            </div>
            <div className="kiro-stat-change kiro-positive">
              <FaArrowUp /> Successfully delivered
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/order-management?source=amazon')}>
          <div className="kiro-stat-icon kiro-blue">
            <FaStore />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Amazon Orders</div>
            <div className="kiro-stat-value">{formatNumber(stats.amazonOrders)}</div>
            <div className="kiro-stat-change kiro-positive">
              <FaShoppingCart /> Marketplace
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/order-management?source=shopify')}>
          <div className="kiro-stat-icon kiro-green">
            <FaStore />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Shopify Orders</div>
            <div className="kiro-stat-value">{formatNumber(stats.shopifyOrders)}</div>
            <div className="kiro-stat-change kiro-positive">
              <FaShoppingCart /> Marketplace
            </div>
          </div>
        </div>

        <div className="kiro-stat-card" onClick={() => navigate('/admin/order-management?marketplace=myntra')}>
          <div className="kiro-stat-icon kiro-orange">
            <FaShoppingBag />
          </div>
          <div className="kiro-stat-content">
            <div className="kiro-stat-label">Myntra Orders</div>
            <div className="kiro-stat-value">{formatNumber(stats.myntraOrders)}</div>
            <div className="kiro-stat-change kiro-positive">
              <FaShoppingCart /> Marketplace
            </div>
          </div>
        </div>
      </div>

      {/* Referral Stats */}
      {referralStats && (
        <div>
          <div className="kiro-section-header">
            <h2 className="kiro-section-title">Referral Statistics</h2>
            <Link to="/admin/referral-management" className="kiro-section-action">
              View All <FaArrowRight />
            </Link>
          </div>
          <div className="kiro-stats-grid kiro-stats-grid--4col">
            <div className="kiro-stat-card" onClick={() => navigate('/admin/referral-management')}>
              <div className="kiro-stat-icon kiro-cyan">
                <FaLink />
              </div>
              <div className="kiro-stat-content">
                <div className="kiro-stat-label">Total Referral Codes</div>
                <div className="kiro-stat-value">{referralStats.totalCodes || 0}</div>
              </div>
            </div>

            <div className="kiro-stat-card" onClick={() => navigate('/admin/referral-management')}>
              <div className="kiro-stat-icon kiro-indigo">
                <FaChartLine />
              </div>
              <div className="kiro-stat-content">
                <div className="kiro-stat-label">Total Visits</div>
                <div className="kiro-stat-value">
                  {formatNumber(referralStats.totalVisits || 0)}
                  {shouldShowActualNumber(referralStats.totalVisits || 0) && (
                    <span className="kiro-stat-actual">({(referralStats.totalVisits || 0).toLocaleString()})</span>
                  )}
                </div>
              </div>
            </div>

            <div className="kiro-stat-card" onClick={() => navigate('/admin/referral-management')}>
              <div className="kiro-stat-icon kiro-pink">
                <FaUserPlus />
              </div>
              <div className="kiro-stat-content">
                <div className="kiro-stat-label">Referral Signups</div>
                <div className="kiro-stat-value">{referralStats.totalSignups || 0}</div>
              </div>
            </div>

            <div className="kiro-stat-card" onClick={() => navigate('/admin/referral-management')}>
              <div className="kiro-stat-icon kiro-green">
                <FaArrowUp />
              </div>
              <div className="kiro-stat-content">
                <div className="kiro-stat-label">Conversion Rate</div>
                <div className="kiro-stat-value">{referralStats.overallConversionRate || 0}%</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Activity & Quick Actions */}
      <div className="kiro-activity-section">
        {/* Recent Orders */}
        <div className="kiro-activity-card">
          <div className="kiro-section-header">
            <h2 className="kiro-section-title">Recent Orders</h2>
            <Link to="/admin/order-management" className="kiro-section-action">
              View All <FaArrowRight />
            </Link>
          </div>
          
          {recentOrders.length > 0 ? (
            <div className="kiro-activity-list">
              {recentOrders.slice(0, 5).map((order, index) => {
                // Get status badge class based on order status
                const getStatusClass = (status) => {
                  const s = (status || '').toLowerCase();
                  if (s === 'delivered' || s === 'completed') return 'kiro-success';
                  if (s === 'processing' || s === 'shipped') return 'kiro-info';
                  if (s === 'payment pending' || s === 'pending') return 'kiro-warning';
                  if (s === 'cancelled' || s === 'failed') return 'kiro-danger';
                  return 'kiro-warning';
                };

                // Get seller name from various possible fields
                const getSellerName = () => {
                  if (order.userId?.firstName) {
                    return `${order.userId.firstName} ${order.userId.lastName || ''}`.trim();
                  }
                  if (order.user?.firstName) {
                    return `${order.user.firstName} ${order.user.lastName || ''}`.trim();
                  }
                  if (order.sellerName) return order.sellerName;
                  if (order.user?.name) return order.user.name;
                  return 'Seller';
                };

                // Get order amount
                const getOrderAmount = () => {
                  return order.totalPrice || order.totalAmount || order.total || 0;
                };

                return (
                  <div key={index} className="kiro-activity-item">
                    <div className={`kiro-activity-icon ${getStatusClass(order.status)}`}>
                      <FaShoppingCart />
                    </div>
                    <div className="kiro-activity-content">
                      <div className="kiro-activity-title">
                        Order #{order.upsaleoOrderId || order.orderId || order._id?.slice(-8)}
                      </div>
                      <div className="kiro-activity-desc">
                        {getSellerName()} • {formatCurrency(getOrderAmount())}
                      </div>
                    </div>
                    <div className="kiro-activity-time">
                      <span className={`kiro-table-badge ${getStatusClass(order.status)}`}>
                        {order.status || 'Pending'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="kiro-empty-state">
              <FaShoppingCart className="kiro-empty-icon" />
              <p>No recent orders</p>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="kiro-activity-card">
          <div className="kiro-section-header">
            <h2 className="kiro-section-title">Quick Actions</h2>
          </div>
          
          <div className="kiro-quick-actions">
            <Link to="/admin/products/add" className="kiro-quick-action-btn">
              <div className="kiro-quick-action-icon">
                <FaPlus />
              </div>
              Add New Product
            </Link>
            
            <Link to="/admin/user-management" className="kiro-quick-action-btn">
              <div className="kiro-quick-action-icon">
                <FaUsers />
              </div>
              Manage Users
            </Link>
            
            <Link to="/admin/order-management" className="kiro-quick-action-btn">
              <div className="kiro-quick-action-icon">
                <FaEye />
              </div>
              View Orders
            </Link>
            
            <Link to="/admin/failed-orders" className="kiro-quick-action-btn">
              <div className="kiro-quick-action-icon">
                <FaBan />
              </div>
              Failed Orders
            </Link>
            
            <Link to="/admin/transactions" className="kiro-quick-action-btn">
              <div className="kiro-quick-action-icon">
                <FaDollarSign />
              </div>
              Transactions
            </Link>
            
            <Link to="/admin/cron-logs" className="kiro-quick-action-btn">
              <div className="kiro-quick-action-icon">
                <FaFileAlt />
              </div>
              System Logs
            </Link>
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="kiro-table-card">
        <div className="kiro-section-header">
          <h2 className="kiro-section-title">System Overview</h2>
        </div>
        
        <table className="kiro-table">
          <thead>
            <tr>
              <th>Module</th>
              <th>Status</th>
              <th>Count</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="kiro-table-product">
                  <FaUsers style={{ color: '#3b82f6' }} />
                  <span className="kiro-table-product-name">User Management</span>
                </div>
              </td>
              <td><span className="kiro-table-badge kiro-success">Active</span></td>
              <td>{stats.totalUsers} users</td>
              <td>
                <Link to="/admin/user-management" className="kiro-section-action">
                  View <FaArrowRight />
                </Link>
              </td>
            </tr>
            <tr>
              <td>
                <div className="kiro-table-product">
                  <FaShoppingCart style={{ color: '#8b5cf6' }} />
                  <span className="kiro-table-product-name">Order Management</span>
                </div>
              </td>
              <td><span className="kiro-table-badge kiro-success">Active</span></td>
              <td>{stats.totalOrders} orders</td>
              <td>
                <Link to="/admin/order-management" className="kiro-section-action">
                  View <FaArrowRight />
                </Link>
              </td>
            </tr>
            <tr>
              <td>
                <div className="kiro-table-product">
                  <FaBox style={{ color: '#f59e0b' }} />
                  <span className="kiro-table-product-name">Product Catalog</span>
                </div>
              </td>
              <td><span className="kiro-table-badge kiro-success">Active</span></td>
              <td>{stats.totalProducts} products</td>
              <td>
                <Link to="/admin/products/list" className="kiro-section-action">
                  View <FaArrowRight />
                </Link>
              </td>
            </tr>
            <tr>
              <td>
                <div className="kiro-table-product">
                  <FaBan style={{ color: '#ef4444' }} />
                  <span className="kiro-table-product-name">Failed Orders</span>
                </div>
              </td>
              <td>
                <span className={`kiro-table-badge ${stats.failedOrders > 0 ? 'kiro-warning' : 'kiro-success'}`}>
                  {stats.failedOrders > 0 ? 'Attention' : 'Clear'}
                </span>
              </td>
              <td>{stats.failedOrders} failed</td>
              <td>
                <Link to="/admin/failed-orders" className="kiro-section-action">
                  View <FaArrowRight />
                </Link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
